import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import WebViewer from "@pdftron/pdfjs-express-viewer";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { Button } from "@components/Button";
import { DocumentViewer } from "@components/DocumentViewer/DocumentViewer";
import { LoaderIcon } from "@components/icons";
import { Notification } from "@components/Notification/Notification";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useConfirmBiometricSigningMutation } from "@services/knowYourCustomer/mutations";
import { useAuthenticationTypesQuery } from "@services/knowYourCustomer/queries";
import type {
  TAuthenticationFormType,
  TAuthenticationType,
} from "@services/knowYourCustomer/types";
import { base64ToArrayBuffer, isNullOrUndefined } from "@utils/common";
import { DOCUMENT_VIEWER_LICENSE_KEY } from "@/constants";

interface IAuthenticationTypeForm {
  setStep: Dispatch<SetStateAction<TAuthenticationFormType>>;
  authPurpose: "identification" | "signDocument";
  onSuccess: () => void;
}

export const AuthenticationTypeForm: React.FC<IAuthenticationTypeForm> = ({
  setStep,
  authPurpose,
  onSuccess,
}) => {
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);
  const [isDocumentLoading, setIsDocumentLoading] = useState(
    authPurpose === "signDocument",
  );
  const { t } = useTranslation();
  const {
    data: authenticationTypesData,
    isPending: isAuthenticationTypesDataLoading,
  } = useAuthenticationTypesQuery(
    authPurpose === "identification"
      ? "authentication-types"
      : "document-signing-types",
  );
  const {
    mutateAsync: confirmBiometricSigning,
    isPending: isBiometricSigningPending,
  } = useConfirmBiometricSigningMutation();
  const { toastApiError } = useApiErrorUtils();

  const availableTypes = useMemo(
    () =>
      authPurpose === "identification"
        ? authenticationTypesData?.authenticationTypes
        : authenticationTypesData?.documentSigningTypes,
    [authPurpose, authenticationTypesData],
  );

  const continueToNextStep = (type: TAuthenticationType) => {
    if (type) {
      setStep(type);
    }
  };

  const viewer = useRef<HTMLDivElement>(null);
  const instance = useRef();
  // Hack to handle race condition, where instance ref is not set, but data is already fetched
  const [instanceChangeTracker, setInstanceChangeTracker] = useState(0);

  /* eslint-disable */
  useEffect(() => {
    if (authPurpose === "identification") {
      return;
    }

    WebViewer(
      {
        path: "/pdfjs",
        licenseKey: DOCUMENT_VIEWER_LICENSE_KEY,
        disabledElements: [
          "header",
          "pageNavOverlay",
          "toolsOverlay",
          "contextMenuPopup",
        ],
      },
      viewer.current,
      // @ts-expect-error - cc
    ).then((inst) => {
      instance.current = inst;
      setInstanceChangeTracker((prev) => prev + 1);

      const { documentViewer } = inst.Core;
      const iframeDoc = inst.UI.iframeWindow.document;
      const FitMode = inst.UI.FitMode;

      iframeDoc
        ?.querySelector(".App")
        ?.setAttribute("style", "background: transparent;");

      documentViewer.addEventListener("documentLoaded", () => {
        setIsDocumentLoading(false);
        inst.UI.setFitMode(FitMode.FitWidth);
      });
    });
  });
  /* eslint-enable */

  useEffect(() => {
    if (authenticationTypesData?.document) {
      // @ts-expect-error - cc
      // eslint-disable-next-line
      instance.current?.loadDocument(
        new Blob([base64ToArrayBuffer(authenticationTypesData.document)]),
      );
    }
  }, [authenticationTypesData, instanceChangeTracker]);

  const submitBiometricSigning = async () => {
    const response = await confirmBiometricSigning().catch(toastApiError);

    if (!isNullOrUndefined(response)) {
      onSuccess();
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {authPurpose === "signDocument" && (
        <>
          <p className="text-gray-900 p-0 text-lg font-semibold">
            {t(
              `KYC.modal.authentication.typeForm.${authPurpose}.documentTitle`,
            )}
          </p>
          <div className="relative h-[200px] w-full">
            <div
              ref={viewer}
              className={twMerge(
                "h-[200px] w-full",
                isDocumentLoading && "hidden",
              )}
            />
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform">
              <LoaderIcon
                className={twMerge(
                  "animate-spin",
                  !isDocumentLoading && "hidden",
                )}
              />
            </div>
          </div>
          <div className="flex h-10 justify-center gap-4">
            {!isDocumentLoading && (
              <>
                <Button
                  className="w-full justify-center"
                  variant="secondary"
                  onClick={() => setIsDocumentViewerOpen(true)}
                >
                  {t(`KYC.modal.authentication.typeForm.actions.reviewForm`)}
                </Button>
                {authenticationTypesData?.wasSignedWithBiometrics && (
                  <Button
                    className="w-full"
                    variant="primary"
                    onClick={submitBiometricSigning}
                    isLoading={isBiometricSigningPending}
                  >
                    {t(`KYC.modal.authentication.typeForm.actions.confirm`)}
                  </Button>
                )}
              </>
            )}
            {authenticationTypesData?.document &&
              isDocumentViewerOpen &&
              createPortal(
                <div className="relative z-[60]">
                  <DocumentViewer
                    file={{
                      data: authenticationTypesData.document,
                      fileName: "",
                    }}
                    onDocumentClose={() => setIsDocumentViewerOpen(false)}
                    isLoading={false}
                    isPdf
                  />
                </div>,
                document.body,
              )}
          </div>
        </>
      )}
      {isAuthenticationTypesDataLoading && (
        <LoaderIcon className="m-auto animate-spin" />
      )}
      {!isAuthenticationTypesDataLoading &&
        !authenticationTypesData?.wasSignedWithBiometrics && (
          <>
            <p className="text-gray-900 p-0 text-lg font-semibold">
              {t(`KYC.modal.authentication.typeForm.${authPurpose}.title`)}
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
              {availableTypes?.map((type, index) => (
                <button
                  key={index}
                  className={twMerge(
                    "flex w-full flex-1 flex-col items-center gap-3 rounded-xl bg-blue-light p-4 pt-8 hover:bg-blue-hover active:bg-blue-light active:ring active:ring-blue-light active:ring-opacity-50",
                  )}
                  onClick={() => continueToNextStep(type)}
                >
                  <img
                    src={`/images/knowYourCustomer/${type}.svg`}
                    alt={type}
                    className="h-[53px]"
                  />
                  <span className="h-max font-bold text-base-black">
                    {t(`KYC.modal.authentication.typeForm.type.${type}`)}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}
      {authenticationTypesData?.authenticationFailure && (
        <Notification
          description={t(
            `KYC.modal.authentication.authenticationFailure.${authenticationTypesData?.authenticationFailure.identificationFailureTypeId}`,
          )}
          color="red"
        />
      )}
    </div>
  );
};
